<template>
  <b-container fluid>
    <b-row class="align-items-center">
      <b-col>
        <b-button size="sm" @click="activePanel = activePanel === 'actions' ? null : 'actions'"
          variant="primary">Actions</b-button>
        <b-button size="sm" @click="activePanel = activePanel === 'users' ? null : 'users'"
          variant="primary">Users</b-button>
      </b-col>
      <b-col sm="2">
        <clearable-input v-model="textFilter" placeholder="Filter..."></clearable-input>
      </b-col>
    </b-row>

    <div class="filter-panel mt-2" :class="{ 'filter-panel-open': activePanel !== null }">
      <div v-show="activePanel === 'actions'" class="filter-panel-content">
        <b-row>
          <b-col>
            <b-form-group align="left">
              <b-form-checkbox-group v-model="selectedActions" :options="availableActionOptions"
                name="activity-action-select" stacked class="checkbox-columns">
              </b-form-checkbox-group>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-button size="sm" variant="secondary" @click="selectedActions = availableActions">All</b-button>
            <b-button size="sm" variant="secondary" @click="invertActions()">Invert</b-button>
          </b-col>
        </b-row>
      </div>
      <div v-show="activePanel === 'users'" class="filter-panel-content">
        <b-row>
          <b-col>
            <b-form-group align="left">
              <b-form-checkbox-group v-model="selectedUsers" :options="availableUsers" name="activity-user-select"
                stacked class="checkbox-columns">
              </b-form-checkbox-group>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-button size="sm" variant="secondary" @click="selectedUsers = availableUsers">All</b-button>
            <b-button size="sm" variant="secondary" @click="invertUsers()">Invert</b-button>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-row class="mt-3">
      <b-col>
        <h2>Activity</h2>
      </b-col>
    </b-row>

    <b-row align-v="center" class="mt-3">
      <b-col sm="2">
        From: <date-picker :value="fromDate" @change="setFrom" />
      </b-col>

      <b-col sm="2">
        To: <date-picker :value="toDate" @change="setTo" />
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col>
        <b-button size="sm" class="mt-1" @click="setRange('today')">Today</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('yesterday')">Yesterday</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisWeek')">This Week</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('lastWeek')">Last Week</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisMonth')">This Month</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('lastMonth')">Last Month</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisYear')">This Year</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('lastYear')">Last Year</b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col class="mt-3">
        <b-table id="activities" striped :items="activities" :fields="fields" :per-page="perPage"
          :current-page="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @sort-changed="onSortChanged"
          :busy="!loaded">
          <template #table-busy>
            <div class="text-center my-3">
              <b-spinner small /> Loading&hellip;
            </div>
          </template>
          <template #cell(user)="{ item }">
            {{ item.user ? item.user.name : '—' }}
          </template>
          <template #cell(action)="{ item }">
            {{ actionLabel(item.action) }}
          </template>
          <template #cell(subject)="{ item }">
            <router-link v-if="item.elevator_id" :to="'/lifts/' + item.elevator_id" class="subject-link">
              {{ item.elevator ? elevatorName(item.elevator) : item.elevator_id }}
            </router-link>
            <router-link v-else-if="item.group_id" :to="'/groups/' + item.group_id" class="subject-link">
              {{ item.group ? item.group.name : ('#' + item.group_id) }}
            </router-link>
            <span v-else-if="item.subject_user_id">
              {{ item.subject_user ? item.subject_user.name : ('#' + item.subject_user_id) }}
            </span>
            <span v-else></span>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row class="mt-2 align-items-center">
      <b-col cols="auto" v-if="activities.length > 15">
        <b-form-select v-model="perPage" :options="perPageOptions" style="width: auto"></b-form-select>
      </b-col>
      <b-col v-if="activities.length > perPage">
        <b-pagination v-model="currentPage" :total-rows="activities.length" :per-page="perPage"
          aria-controls="activities" class="mb-0"></b-pagination>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import api from './api.js'
import moment from 'moment';
import DatePicker from './DatePicker.vue'
import ClearableInput from './ClearableInput.vue'
import { openWS } from './ws.js'

const ACTION_LABELS = {
  login: 'login',
  create_user: 'created user',
  update_user: 'updated user',
  delete_user: 'deleted user',
  update_elevator: 'updated elevator',
  delete_elevator: 'deleted elevator',
  create_group: 'created group',
  update_group: 'updated group',
  delete_group: 'deleted group',
  set_relay: 'set relay',
  call_elevator: 'called elevator',
  set_disabled: 'set disabled',
  set_evac_group: 'set group evac',
  set_evac_elevator: 'set elevator evac',
  reset_elevator: 'reset elevator',
  controller_reset: 'controller reset',
  clear_errors: 'cleared errors',
  view_events: 'viewed events',
  view_inputs: 'viewed inputs',
  view_elevator: 'viewed lift',
  view_status: 'viewed status',
  view_group: 'viewed group',
  view_breakdowns: 'viewed breakdowns',
  view_maintenance: 'viewed maintenance',
  view_activity: 'viewed activity',
  start_tunnel: 'started tunnel',
  stop_tunnel: 'stopped tunnel',
  start_interact: 'started interact',
  stop_interact: 'stopped interact',
  set_position_label: 'set position label',
  ignore_elevator: 'ignored elevator',
  unignore_elevator: 'unignored elevator',
  update_unconnected: 'updated unconnected',
  set_planned_maintenance: 'set planned maintenance',
  delete_planned_maintenance: 'deleted planned maintenance',
  complete_maintenance: 'completed maintenance',
  update_settings: 'updated settings',
  send_support: 'sent support email',
  generate_report: 'generated report',
  update_profile: 'updated profile',
};

export default {
  name: 'activity',
  components: { DatePicker, ClearableInput },
  data() {
    return {
      currentPage: 1,
      perPage: parseInt(localStorage.getItem('activityPerPage')) || 15,
      perPageOptions: [
        { value: 15, text: '15' },
        { value: 30, text: '30' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      sortBy: localStorage.getItem('activitySortBy') || 'time',
      sortDesc: localStorage.getItem('activitySortDesc') !== 'false',
      textFilter: (window.history.state && window.history.state.textFilter) || '',
      activePanel: null,
      selActions: localStorage.getItem('activitySelActions') !== null ? JSON.parse(localStorage.getItem('activitySelActions')) : null,
      selUsers: localStorage.getItem('activitySelUsers') !== null ? JSON.parse(localStorage.getItem('activitySelUsers')) : null,
      loaded: false,
      activities: [],
      cachedActions: JSON.parse(localStorage.getItem('activityCachedActions') || '[]'),
      cachedUsers: JSON.parse(localStorage.getItem('activityCachedUsers') || '[]'),
      localFrom: null,
      localTo: null,
      wsHandle: null,
    }
  },
  computed: {
    fromDate() { return this.from ? moment(this.from).format('YYYY-MM-DD') : '' },
    toDate() { return this.to ? moment(this.to).format('YYYY-MM-DD') : '' },
    fields() {
      return [
        { key: 'time', sortable: true, label: 'Time', formatter: v => moment(v).format('YYYY-MM-DD HH:mm:ss') },
        { key: 'user', sortable: true, label: 'User', sortByFormatted: true, formatter: (v, k, item) => item.user ? item.user.name : '' },
        { key: 'action', sortable: true, label: 'Action', sortByFormatted: true, formatter: v => ACTION_LABELS[v] || v },
        { key: 'details', sortable: true, label: 'Details' },
        {
          key: 'subject', sortable: true, label: 'Subject', sortByFormatted: true,
          formatter: (v, k, item) => (item.elevator && this.elevatorName(item.elevator)) || (item.group && item.group.name) || (item.subject_user && item.subject_user.name) || ''
        },
      ];
    },
    from: {
      get() { return this.localFrom; },
      set(v) { this.localFrom = v; }
    },
    to: {
      get() { return this.localTo; },
      set(v) { this.localTo = v; }
    },
    // Derived from loaded data for the checkbox UI only.
    availableActions() {
      return this.cachedActions;
    },
    availableActionOptions() {
      return this.cachedActions.map(a => ({ value: a, text: ACTION_LABELS[a] || a }));
    },
    availableUsers() {
      return this.cachedUsers;
    },
    selectedActions: {
      get() { return this.selActions !== null ? this.selActions : this.availableActions; },
      set(v) {
        if (this.availableActions.length === 0) return;
        const isAll = v.length === 0 || v.length >= this.availableActions.length;
        const effective = isAll ? null : v;
        if (effective === null && this.selActions === null) return;
        if (effective !== null && this.selActions !== null && effective.length === this.selActions.length && effective.every((x, i) => x === this.selActions[i])) return;
        this.selActions = effective;
        if (effective !== null) {
          localStorage.setItem('activitySelActions', JSON.stringify(effective));
        } else {
          localStorage.removeItem('activitySelActions');
        }
      },
    },
    selectedUsers: {
      get() { return this.selUsers !== null ? this.selUsers : this.availableUsers; },
      set(v) {
        if (this.availableUsers.length === 0) return;
        const isAll = v.length === 0 || v.length >= this.availableUsers.length;
        const effective = isAll ? null : v;
        if (effective === null && this.selUsers === null) return;
        if (effective !== null && this.selUsers !== null && effective.length === this.selUsers.length && effective.every((x, i) => x === this.selUsers[i])) return;
        this.selUsers = effective;
        if (effective !== null) {
          localStorage.setItem('activitySelUsers', JSON.stringify(effective));
        } else {
          localStorage.removeItem('activitySelUsers');
        }
      },
    },
  },
  watch: {
    perPage(v) { localStorage.setItem('activityPerPage', String(v)); this.currentPage = 1; },
    selActions() { if (this._initialized) this.getActivities(); },
    selUsers() { if (this._initialized) this.getActivities(); },
    textFilter() {
      if (!this._initialized) return;
      const state = Object.assign({}, window.history.state, { textFilter: this.textFilter });
      window.history.replaceState(state, '');
      clearTimeout(this._textFilterTimer);
      this._textFilterTimer = setTimeout(() => this.getActivities(), 500);
    },
  },
  methods: {
    onActivityMessage(msg) {
      if (msg.type !== 'activity') return;
      const a = msg.data;
      const t = new Date(a.time).getTime();
      const from = this.from ? new Date(this.from).getTime() : -Infinity;
      const to = this.to ? new Date(this.to).getTime() : Infinity;
      if (t >= from && t <= to) {
        this.activities = [a].concat(this.activities);
        if (a.action && !this.cachedActions.includes(a.action)) {
          this.cachedActions = [...this.cachedActions, a.action].sort();
          localStorage.setItem('activityCachedActions', JSON.stringify(this.cachedActions));
        }
        const userName = a.user ? a.user.name : '';
        if (userName && !this.cachedUsers.includes(userName)) {
          this.cachedUsers = [...this.cachedUsers, userName].sort();
          localStorage.setItem('activityCachedUsers', JSON.stringify(this.cachedUsers));
        }
      }
    },
    actionLabel(action) {
      return ACTION_LABELS[action] || action;
    },
    invertActions() {
      const inverted = this.availableActions.filter(a => !(this.selActions || this.availableActions).includes(a));
      this.selectedActions = inverted.length < this.availableActions.length ? inverted : [];
    },
    invertUsers() {
      const inverted = this.availableUsers.filter(u => !(this.selUsers || this.availableUsers).includes(u));
      this.selectedUsers = inverted.length < this.availableUsers.length ? inverted : [];
    },
    setFrom(v) {
      this.from = v ? moment(v).startOf('day').format() : null;
      this.getActivities();
    },
    setTo(v) {
      this.to = v ? moment(v).endOf('day').format() : null;
      this.getActivities();
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
      localStorage.setItem('activitySortBy', sortBy);
      localStorage.setItem('activitySortDesc', sortDesc);
    },
    getActivities() {
      const PAGE_SIZE = 1000;
      this.currentPage = 1;
      this.loaded = false;

      // Stamp this fetch so stale responses from a previous call are discarded.
      const fetchId = ++this._fetchId;

      const filters = {
        actions: this.selActions !== null ? this.selActions : [],
        users: this.selUsers !== null ? this.selUsers : [],
        text: this.textFilter,
      };

      const drain = (cursor, acc) => {
        api.getActivities(this.from, this.to, cursor, PAGE_SIZE, filters, resp => {
          if (fetchId !== this._fetchId) return; // stale
          if (!resp.data) { this.activities = acc; this.loaded = true; if (!this._initialized) this.$nextTick(() => { this._initialized = true; }); return; }
          const page = resp.data.data || [];
          const all = acc.concat(page);
          if (resp.data.has_more && resp.data.next_cursor) {
            drain(resp.data.next_cursor, all);
          } else {
            this.activities = all;
            // Accumulate checkbox options — union with existing cache so unchecking
            // a filter doesn't make options disappear.
            const newActions = all.map(a => a.action);
            const newUsers = all.map(a => a.user ? a.user.name : '').filter(Boolean);
            this.cachedActions = [...new Set([...this.cachedActions, ...newActions])].sort();
            this.cachedUsers = [...new Set([...this.cachedUsers, ...newUsers])].sort();
            localStorage.setItem('activityCachedActions', JSON.stringify(this.cachedActions));
            localStorage.setItem('activityCachedUsers', JSON.stringify(this.cachedUsers));
            this.loaded = true;
            if (!this._initialized) this.$nextTick(() => { this._initialized = true; });
          }
        });
      };

      drain(null, []);
    },
    setRange(range) {
      switch (range) {
        case 'today':
          this.from = moment().startOf('day').format();
          this.to = moment().endOf('day').format();
          break;
        case 'yesterday':
          this.from = moment().startOf('day').subtract(1, 'day').format();
          this.to = moment().endOf('day').subtract(1, 'day').format();
          break;
        case 'thisWeek':
          this.from = moment().startOf('week').format();
          this.to = moment().endOf('day').format();
          break;
        case 'lastWeek':
          this.from = moment().startOf('week').subtract(1, 'week').format();
          this.to = moment().endOf('week').subtract(1, 'week').format();
          break;
        case 'thisMonth':
          this.from = moment().startOf('month').format();
          this.to = moment().endOf('day').format();
          break;
        case 'lastMonth':
          this.from = moment().startOf('month').subtract(1, 'month').format();
          this.to = moment().subtract(1, 'month').endOf('month').format();
          break;
        case 'thisYear':
          this.from = moment().startOf('year').format();
          this.to = moment().endOf('day').format();
          break;
        case 'lastYear':
          this.from = moment().startOf('year').subtract(1, 'year').format();
          this.to = moment().endOf('year').subtract(1, 'year').format();
          break;
      }
      this.currentPage = 1;
      this.getActivities();
    },
  },
  beforeRouteLeave(to, from, next) {
    history.replaceState(Object.assign({}, history.state, { activityFrom: this.from, activityTo: this.to }), '');
    next();
  },
  created() {
    this._fetchId = 0;
    this._initialized = false;
    this._textFilterTimer = null;
    const state = history.state || {};
    if (state.activityFrom) {
      this.from = state.activityFrom;
      this.to = state.activityTo;
    } else {
      this.from = moment().startOf('day').format();
      this.to = moment().endOf('day').format();
    }
    this.getActivities();
  },
  mounted() {
    this.wsHandle = openWS('/api/v1/ws/activities', this.onActivityMessage);
  },
  beforeDestroy() {
    if (this.wsHandle) this.wsHandle.close();
  },
}
</script>

<style>
.subject-link {
  color: inherit;
  text-decoration: none;
}

.subject-link:hover {
  text-decoration: underline;
}

.filter-panel {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.filter-panel-open {
  max-height: 500px;
}

.filter-panel-content {
  border: 1px solid #dee2e6;
  border-radius: 0 0.25rem 0.25rem 0.25rem;
  padding: 0.75rem 1rem 0.5rem;
}

.checkbox-columns {
  column-count: 4;
  column-gap: 1.5rem;
}

@media (max-width: 768px) {
  .checkbox-columns {
    column-count: 2;
  }
}
</style>



// WEBPACK FOOTER //
// src/Activity.vue